/** C1 — the bankruptcy-petition hub for a file at "Prepare bankruptcy petition" / "Awaiting court hearing": the five steps
 *  (Prepared → Filed → Served → Certificate → Hearing), gates G6 / G7 / G8, the files saved and the open petition actions. */
import { useEffect, useState } from "react";
import { CheckCircle2, ChevronRight, Circle, Coins, ExternalLink, Loader2, Lock, ScrollText, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getPetitionState } from "@/lib/api";
import GateChecklistDialog from "@/pages/legal/GateChecklistDialog";
import ServiceRecordDialog from "@/pages/legal/ServiceRecordDialog";
import PetitionDetailsDialog from "./PetitionDetailsDialog";
import PetitionFilingDialog from "./PetitionFilingDialog";
import PetitionCertificateDialog from "./PetitionCertificateDialog";
import HearingDateDialog from "./HearingDateDialog";
import HearingOutcomeDialog from "./HearingOutcomeDialog";
import MenziesPackDialog from "./MenziesPackDialog";
import CourtCostsDialog from "./CourtCostsDialog";
import { errMsg, fmtDate, money } from "./petitionShared";

const NEXT_AFTER_GATE = { G6: "details", G7: "service" };
const OUTCOME_TONE = { order_made: "text-slate-800", adjourned: "text-amber-700", dismissed: "text-rose-700", withdrawn: "text-indigo-700" };

function OutcomeHistory({ outcomes }) {
  if (!outcomes?.length) return null;
  return (
    <div className="border rounded p-2 bg-slate-50/60 space-y-0.5" data-testid="petition-outcomes">
      <div className="text-[10px] font-medium text-slate-600">Court outcomes</div>
      {outcomes.map((o) => (
        <div key={o.id} className="text-[11px]" data-testid={`petition-outcome-${o.outcome}`}>
          <span className={cn("font-medium", OUTCOME_TONE[o.outcome])}>{o.label}</span>
          {o.hearing_date && <span className="text-slate-500"> · hearing {fmtDate(o.hearing_date)}</span>}
          {o.reason_label && <span className="text-slate-600"> — {o.reason_label}{o.reason_text ? `: ${o.reason_text}` : ""}</span>}
          {o.outcome === "dismissed" && o.reason_text && <span className="text-slate-600"> — {o.reason_text}</span>}
          {o.new_date && <span className="text-slate-600"> · re-listed {fmtDate(o.new_date)}</span>}
          {o.outcome === "adjourned" && !o.new_date && <span className="text-slate-600"> · date to be fixed</span>}
          {o.order_date && <span className="text-slate-600"> · order {fmtDate(o.order_date)}</span>}
          {o.costs_awarded ? <span className="text-slate-600"> · costs awarded {money(o.costs_awarded)}</span> : null}
          {o.costs_amount ? <span className="text-slate-600"> · costs against us {money(o.costs_amount)}</span> : null}
          {(o.letters || []).map((l, i) => <a key={i} href={l.web_url || "#"} target="_blank" rel="noreferrer" className="ml-1 text-indigo-700 hover:underline" data-testid={`petition-outcome-letter-${o.outcome}-${i}`}>· {l.filename || l.kind}</a>)}
          <span className="text-slate-400"> · {o.by} {fmtDate(o.at)}</span>
        </div>
      ))}
    </div>
  );
}

function GateChip({ gate, ev, onClick }) {
  if (!ev) return null;
  const open = (ev.blocking || []).length;
  return (
    <button type="button" onClick={onClick} data-testid={`petition-gate-${gate}`}
            className={cn("inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border", ev.passed ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200")}
            title={ev.passed ? `${gate} — every item satisfied` : `${gate} — ${ev.blocking.join("; ")}`}>
      {ev.passed ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}{gate}{!ev.passed && ` · ${open} open`}
    </button>
  );
}

function StepRow({ step, gateEv, onAction, onGate }) {
  const Icon = step.done ? CheckCircle2 : step.available ? Circle : Lock;
  return (
    <li className={cn("px-3 py-2 flex items-start gap-2", step.available && "bg-violet-50/40")} data-testid={`petition-step-${step.key}`}>
      <Icon className={cn("w-4 h-4 mt-0.5 shrink-0", step.done ? "text-emerald-600" : step.available ? "text-violet-700" : "text-slate-300")} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn("text-xs font-medium", step.done && "text-slate-500")}>{step.label}</span>
          {step.gate && <GateChip gate={step.gate} ev={gateEv} onClick={() => onGate(step.gate)} />}
          {step.done && step.at && <span className="ml-auto text-[10px] text-slate-500" data-testid={`petition-step-done-${step.key}`}>{step.by || "—"} · {fmtDate(step.at)}</span>}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`petition-step-detail-${step.key}`}>{step.detail}</div>
      </div>
      {step.available && (
        <Button size="sm" className="h-7 text-[11px] bg-violet-700 hover:bg-violet-800 shrink-0" onClick={() => onAction(step)} data-testid={`petition-action-${step.key}`}>
          {step.action}<ChevronRight className="w-3 h-3 ml-0.5" />
        </Button>
      )}
    </li>
  );
}

function FilesStrip({ petition }) {
  const files = [...((petition?.prepared || {}).files || []), ...((petition?.filing || {}).sealed_copy || []).map((f) => ({ ...f, kind: "sealed" })), ...((petition?.certificate || {}).files || []).map((f) => ({ ...f, kind: "certificate" }))];
  if (!files.length) return null;
  const tag = { bank1: "Bank 1", bank5: "Bank 5", sealed: "Sealed copy", certificate: "Certificate", other: "Doc" };
  return (
    <div className="flex flex-wrap gap-1" data-testid="petition-files">
      {files.map((f, i) => (
        <a key={`${f.file_id || f.name}-${i}`} href={f.web_url || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] border rounded bg-white px-1.5 py-0.5 text-indigo-700 hover:underline max-w-[240px]" data-testid={`petition-file-${i}`}>
          <ExternalLink className="w-3 h-3 shrink-0" /><span className="font-medium shrink-0">{tag[f.kind] || "Doc"}</span><span className="truncate">{f.name}</span>
        </a>
      ))}
    </div>
  );
}

export default function PetitionHubDialog({ caseRow, onClose, onRouteChanged }) {
  const [state, setState] = useState(null);
  const [dlg, setDlg] = useState(null);   // {kind: details|filing|service|certificate|hearing|gate, gate?, then?}
  const load = async () => { try { setState(await getPetitionState(caseRow.case_id)); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseRow.case_id]);

  const saved = async (r) => { setDlg(null); await load(); if (r?.route) await onRouteChanged?.(r.route); else await onRouteChanged?.(); };
  const onAction = (step) => {
    if (step.key === "prepared") setDlg({ kind: "gate", gate: "G6", then: "details" });
    else if (step.key === "served") setDlg({ kind: "gate", gate: "G7", then: "service" });
    else setDlg({ kind: step.key === "filed" ? "filing" : step.key });
  };
  const onGate = (gate) => setDlg({ kind: "gate", gate, then: NEXT_AFTER_GATE[gate] && state?.steps?.find((s) => s.gate === gate)?.available ? NEXT_AFTER_GATE[gate] : null });
  const proceedLabel = { details: "Record petition details…", service: "Record petition service…", hearing: "Record hearing date…", outcome: "Record hearing outcome…", withdraw: "Withdraw the petition…", menzies: "Open the claim pack…" };
  const terminal = ["order_made", "dismissed", "withdrawn"].includes(state?.petition?.outcomes?.slice(-1)[0]?.outcome);
  const canWithdraw = state && !terminal && ((state.route_status === "prepare_petition" && !!state.petition?.filing?.filed_on) || state.route_status === "awaiting_hearing");

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && !dlg && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="petition-hub-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2 flex-wrap">
              <ScrollText className="w-4 h-4 text-violet-700" />Bankruptcy petition — {caseRow.customer_name}
              {state && <span className="text-[11px] font-normal text-muted-foreground">· {state.route_label || "not on the route"} · balance {money(state.balance)}{state.legal_owner ? ` · owner ${state.legal_owner}` : ""}</span>}
            </DialogTitle>
          </DialogHeader>
          {!state ? <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Reading the file…</div> : (
            <div className="space-y-3">
              {!state.allowed && !["order_made", "referred_menzies"].includes(state.route_status) && <div className="text-xs border border-amber-300 bg-amber-50 text-amber-900 rounded p-2" data-testid="petition-not-allowed">The petition steps are recorded while the file is at 'Prepare bankruptcy petition' (and the certificate while 'Awaiting court hearing').</div>}
              <ul className="divide-y border rounded" data-testid="petition-steps">
                {state.steps.map((s) => <StepRow key={s.key} step={s} gateEv={s.gate ? state.gates?.[s.gate] : null} onAction={onAction} onGate={onGate} />)}
              </ul>
              {state.open_actions?.length > 0 && (
                <div className="text-[11px] text-slate-700 border rounded p-2 bg-slate-50/60" data-testid="petition-open-actions">
                  {state.open_actions.map((a) => <div key={a.event_id} data-testid={`petition-open-action-${a.source_rule}`}>⏱ <strong>{a.custom_label}</strong> — due {fmtDate(a.date)} · {a.owner || "unassigned"}{a.mandatory ? " · mandatory" : ""}</div>)}
                </div>
              )}
              <FilesStrip petition={state.petition} />
              <OutcomeHistory outcomes={state.petition?.outcomes} />
              {canWithdraw && (
                <div className="flex items-center justify-between gap-2 border border-dashed rounded p-2 text-[11px] text-slate-600" data-testid="petition-withdraw-row">
                  <span>Debtor paid or settled, or the petition is no longer worth pursuing? Withdraw it with a Form IAA (gate G11) — the route closes.</span>
                  <Button size="sm" variant="outline" className="h-7 text-[11px] shrink-0 text-indigo-800 border-indigo-300" onClick={() => setDlg({ kind: "outcome", preset: "withdrawn" })} data-testid="petition-action-withdraw">Withdraw petition (Form IAA)…</Button>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground">Fee {money(state.thresholds?.fee_gbp)} · deposit {money(state.thresholds?.deposit_gbp)} · service ≥ {state.thresholds?.min_service_days_before_hearing} days before the hearing · "File petition" SLA {state.thresholds?.file_petition_sla_wd} wd · sealed-order chase {state.thresholds?.sealed_order_chase_wd} wd · re-list chase {state.thresholds?.relist_chase_wd} wd — editable in Legal process settings → Thresholds.</p>
            </div>
          )}
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" size="sm" className="text-amber-800 border-amber-300" onClick={() => setDlg({ kind: "costs" })} data-testid="petition-court-costs">
              <Coins className="w-3.5 h-3.5 mr-1" />Court costs{state?.costs_total ? ` · ${money(state.costs_total)}` : ""}
            </Button>
            <Button variant="outline" size="sm" onClick={onClose} data-testid="petition-hub-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {dlg?.kind === "gate" && (
        <GateChecklistDialog caseRow={caseRow} gate={dlg.gate} onClose={() => setDlg(null)} proceedLabel={proceedLabel[dlg.then]}
          onPassed={dlg.then ? () => setDlg(dlg.then === "withdraw" ? { kind: "outcome", preset: "withdrawn" } : { kind: dlg.then }) : undefined} />
      )}
      {dlg?.kind === "details" && <PetitionDetailsDialog caseRow={caseRow} state={state} onClose={() => setDlg(null)} onSaved={saved} onGate={(g) => setDlg({ kind: "gate", gate: g, then: "details" })} />}
      {dlg?.kind === "filing" && <PetitionFilingDialog caseRow={caseRow} state={state} onClose={() => setDlg(null)} onSaved={saved} />}
      {dlg?.kind === "service" && (
        <ServiceRecordDialog caseRow={caseRow} methods={state?.service_methods || []} document="petition" onClose={() => setDlg(null)} onSaved={saved}
          onGateBlocked={(g) => setDlg({ kind: "gate", gate: g, then: "service" })} />
      )}
      {dlg?.kind === "certificate" && <PetitionCertificateDialog caseRow={caseRow} onClose={() => setDlg(null)} onSaved={saved} />}
      {dlg?.kind === "hearing" && <HearingDateDialog caseRow={caseRow} state={state} onClose={() => setDlg(null)} onSaved={saved} onGate={(g) => setDlg({ kind: "gate", gate: g, then: "hearing" })} />}
      {dlg?.kind === "outcome" && <HearingOutcomeDialog caseRow={caseRow} preset={dlg.preset} onClose={() => setDlg(null)} onSaved={saved} onGate={(g) => setDlg({ kind: "gate", gate: g, then: "withdraw" })} />}
      {dlg?.kind === "menzies" && <MenziesPackDialog caseRow={caseRow} onClose={async () => { setDlg(null); await load(); }} onDone={saved} onGate={(g) => setDlg({ kind: "gate", gate: g, then: "menzies" })} />}
      {dlg?.kind === "costs" && <CourtCostsDialog caseRow={caseRow} onClose={async () => { setDlg(null); await load(); }} onChanged={() => onRouteChanged?.()} />}
    </>
  );
}
